"use client";

import { useState, useSyncExternalStore } from "react";
import {
  parseLocalDate,
  planForDeadline,
  planForPace,
} from "@/lib/writingPace";
import { useI18n } from "@/components/providers/I18nProvider";
import { uiIntlLocale } from "@/lib/i18n/languages";

function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const subscribeNever = () => () => {};

/**
 * The visitor's local date as YYYY-MM-DD — the calculator works in whole days.
 * Null on the server and while hydrating: the server's today is its own
 * clock's, which is a different day from the visitor's for part of every day.
 */
function useTodayIso(): string | null {
  return useSyncExternalStore(subscribeNever, () => isoDate(new Date()), () => null);
}

const LONG_DATE: Intl.DateTimeFormatOptions = {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
};

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  step,
  suffix,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (n: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <input
          id={id}
          type="number"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
          className="min-h-[40px] w-full rounded-lg border border-border bg-background px-3 text-sm tabular-nums"
        />
        {suffix && <span className="text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </div>
  );
}

/**
 * Two questions, one set of inputs: "I have a deadline, what is my daily
 * target?" and "this is my pace, when do I finish?"
 *
 * Both are the same search — people typing "word count goal calculator" arrive
 * with one or the other in mind, and answering only one sends half of them
 * back to the results page.
 */
export function WordCountGoalCalculator() {
  const { locale, t } = useI18n();
  const numberLocale = uiIntlLocale(locale);
  const formatNumber = (value: number) => value.toLocaleString(numberLocale);
  const formatDuration = (calendarDays: number) => {
    if (calendarDays <= 0) return t("marketing.wordCount.today");
    if (calendarDays === 1) return t("marketing.wordCount.oneDay");
    if (calendarDays < 14) return t("marketing.wordCount.days", { count: formatNumber(calendarDays) });
    if (calendarDays < 60) {
      const weeks = Math.round(calendarDays / 7);
      return t(weeks === 1 ? "marketing.wordCount.oneWeek" : "marketing.wordCount.weeks", {
        count: formatNumber(weeks),
      });
    }
    if (calendarDays < 365) {
      const months = Math.round(calendarDays / 30.44);
      return t(months === 1 ? "marketing.wordCount.oneMonth" : "marketing.wordCount.months", {
        count: formatNumber(months),
      });
    }
    const years = Math.round((calendarDays / 365.25) * 10) / 10;
    return t(years === 1 ? "marketing.wordCount.oneYear" : "marketing.wordCount.years", {
      count: formatNumber(years),
    });
  };
  const todayIso = useTodayIso();
  const start = todayIso ? parseLocalDate(todayIso) : null;
  const [target, setTarget] = useState(90_000);
  const [written, setWritten] = useState(0);
  const [daysPerWeek, setDaysPerWeek] = useState(5);
  const [pickedDeadline, setDeadline] = useState<string | null>(null);
  const [perDay, setPerDay] = useState(1000);

  const sixMonthsOut = start ? new Date(start.getFullYear(), start.getMonth() + 6, start.getDate()) : null;
  const deadline = pickedDeadline ?? (sixMonthsOut ? isoDate(sixMonthsOut) : "");
  const input = { target, written, daysPerWeek };
  const deadlineDate = parseLocalDate(deadline);
  const byDeadline = start && deadlineDate ? planForDeadline(input, start, deadlineDate) : null;
  const byPace = start ? planForPace(input, start, perDay) : null;

  const pct = target > 0 ? Math.min(100, Math.round((written / target) * 100)) : 0;

  return (
    <div className="mt-6">
      <div className="grid gap-4 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
        <NumberField
          id="wc-target"
          label={t("marketing.wordCount.target")}
          value={target}
          onChange={setTarget}
          min={0}
          max={500_000}
          step={5000}
        />
        <NumberField
          id="wc-written"
          label={t("marketing.wordCount.written")}
          value={written}
          onChange={setWritten}
          min={0}
          max={500_000}
          step={1000}
        />
        <NumberField
          id="wc-dpw"
          label={t("marketing.wordCount.daysPerWeek")}
          value={daysPerWeek}
          onChange={(n) => setDaysPerWeek(Math.min(7, Math.max(1, n)))}
          min={1}
          max={7}
        />
      </div>

      {target > 0 && (
        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-accent transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
            {t("marketing.wordCount.progress", {
              written: formatNumber(written),
              target: formatNumber(target),
              percent: formatNumber(pct),
              remaining: formatNumber(target - written),
            })}
          </p>
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {/* Deadline → daily target */}
        <section className="rounded-xl border border-border p-4">
          <h3 className="font-semibold">{t("marketing.wordCount.deadline")}</h3>
          <label htmlFor="wc-deadline" className="mt-3 block text-sm font-medium">
            {t("marketing.wordCount.finishBy")}
          </label>
          <input
            id="wc-deadline"
            type="date"
            value={deadline}
            min={todayIso ?? undefined}
            onChange={(e) => setDeadline(e.target.value)}
            className="mt-1 min-h-[40px] w-full rounded-lg border border-border bg-background px-3 text-sm"
          />
          <div data-testid="wc-deadline-result" className="mt-4">
            {!start ? null : !byDeadline ? (
              <p className="text-sm text-muted-foreground">{t("marketing.wordCount.pickDate")}</p>
            ) : byDeadline.done ? (
              <p className="text-sm">{t("marketing.wordCount.alreadyThereBy", { words: formatNumber(written - target) })}</p>
            ) : byDeadline.impossible ? (
              <p className="text-sm text-amber-700 dark:text-amber-500">
                {t("marketing.wordCount.noWritingDays")}
              </p>
            ) : (
              <>
                <p className="text-3xl font-bold tabular-nums">
                  {formatNumber(byDeadline.wordsPerDay)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("marketing.wordCount.wordsPerWritingDay")}
                </p>
                <p className="mt-2 text-sm text-muted-foreground tabular-nums">
                  {t("marketing.wordCount.writingDaysAcross", {
                    days: formatNumber(byDeadline.writingDays),
                    duration: formatDuration(byDeadline.calendarDays),
                  })}
                </p>
              </>
            )}
          </div>
        </section>

        {/* Pace → finish date */}
        <section className="rounded-xl border border-border p-4">
          <h3 className="font-semibold">{t("marketing.wordCount.pace")}</h3>
          <NumberField
            id="wc-perday"
            label={t("marketing.wordCount.wordsPerWritingDay")}
            value={perDay}
            onChange={setPerDay}
            min={0}
            max={20_000}
            step={100}
          />
          <div data-testid="wc-pace-result" className="mt-4">
            {!start ? null : !byPace ? (
              <p className="text-sm text-muted-foreground">
                {t("marketing.wordCount.setDailyAboveZero")}
              </p>
            ) : byPace.done ? (
              <p className="text-sm">{t("marketing.wordCount.nothingLeft")}</p>
            ) : (
              <>
                <p className="text-2xl font-bold">
                  {byPace.finish.toLocaleDateString(numberLocale, LONG_DATE)}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("marketing.wordCount.fromToday", { duration: formatDuration(byPace.calendarDays) })}
                </p>
                <p className="mt-2 text-sm text-muted-foreground tabular-nums">
                  {t("marketing.wordCount.writingDaysAt", {
                    days: formatNumber(byPace.writingDays),
                    words: formatNumber(perDay),
                  })}
                </p>
              </>
            )}
          </div>
        </section>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        {t("marketing.wordCount.helper")}
      </p>
    </div>
  );
}
